import React, { useState, useRef, useEffect } from 'react';
import { Bot, X, Send, BarChart3, TrendingUp, TrendingDown, Minus, Sparkles } from 'lucide-react';
import api from '../../utils/api';

// ============================================
// Analytics AI Panel
// ============================================
// Chat interface for analytics questions.
// Displays key metrics, insights, and suggestions.
// ============================================

const INSIGHT_COLORS = {
    overview: 'bg-blue-50 border-blue-200 text-blue-700',
    comparison: 'bg-purple-50 border-purple-200 text-purple-700',
    trend: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    risk: 'bg-red-50 border-red-200 text-red-700',
    recommendation: 'bg-amber-50 border-amber-200 text-amber-700',
};

const TREND_ICONS = {
    up: TrendingUp,
    down: TrendingDown,
    stable: Minus,
};

const SUGGESTION_PROMPTS = [
    'How is onboarding going?',
    'Any at-risk employees?',
    'Compare team performance',
    'Show compliance status',
    'Monthly summary',
];

const AnalyticsAIPanel = ({ isOpen, onClose, initialQuestion = null }) => {
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isSending, setIsSending] = useState(false);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    useEffect(() => {
        if (isOpen) inputRef.current?.focus();
    }, [isOpen]);

    // Handle initial question from contextual card
    useEffect(() => {
        if (initialQuestion && isOpen && messages.length === 0) {
            sendMessage(null, initialQuestion);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialQuestion, isOpen]);

    async function sendMessage(e, overrideMessage = null) {
        if (e) e.preventDefault();
        const userMessage = overrideMessage || inputMessage.trim();
        if (!userMessage) return;

        if (!overrideMessage) setInputMessage('');

        setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
        setIsSending(true);

        try {
            const response = await api.post('/admin/ai/analytics/chat', {
                message: userMessage,
                chat_history: messages.map(m => ({ role: m.role, content: typeof m.content === 'string' ? m.content : m.content.message })),
            });

            if (response.data.success) {
                setMessages(prev => [...prev, {
                    role: 'assistant',
                    content: response.data.response,
                }]);
            }
        } catch (error) {
            console.error('Analytics chat error:', error);
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: { message: 'Something went wrong analyzing your data. Please try again.', insight_type: 'overview', key_metrics: [], suggestions: [] },
            }]);
        } finally {
            setIsSending(false);
        }
    }

    if (!isOpen) return null;

    return (
        <div className="fixed inset-y-0 right-0 w-full max-w-lg bg-card border-l border-border shadow-2xl z-50 flex flex-col animate-slide-in-right">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white">
                <div className="flex items-center gap-2">
                    <BarChart3 size={20} />
                    <span className="font-semibold">Analytics AI</span>
                </div>
                <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/20 transition-colors cursor-pointer">
                    <X size={18} />
                </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.length === 0 && (
                    <div className="text-center py-8">
                        <div className="w-14 h-14 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <Sparkles size={24} className="text-indigo-500" />
                        </div>
                        <h3 className="text-lg font-semibold text-foreground mb-2">Ask me about your data</h3>
                        <p className="text-sm text-muted-foreground mb-6">I can analyze onboarding progress, compare teams, spot risks, and generate insights.</p>
                        <div className="flex flex-wrap justify-center gap-2">
                            {SUGGESTION_PROMPTS.map((prompt, i) => (
                                <button
                                    key={i}
                                    onClick={() => sendMessage(null, prompt)}
                                    className="px-3 py-2 bg-card border border-border rounded-lg text-xs text-muted-foreground hover:text-foreground hover:border-indigo-300 cursor-pointer transition-all"
                                >
                                    {prompt}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {messages.map((msg, index) => (
                    <div key={index} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                        {msg.role === 'assistant' && (
                            <div className="w-7 h-7 min-w-[28px] rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center text-white">
                                <BarChart3 size={14} />
                            </div>
                        )}
                        <div className={`max-w-[90%] ${msg.role === 'user' ? '' : ''}`}>
                            {msg.role === 'user' ? (
                                <div className="px-4 py-3 bg-primary text-white rounded-2xl rounded-br-md text-sm">
                                    {msg.content}
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {/* Message text */}
                                    <div className="px-4 py-3 bg-muted/50 border border-border rounded-2xl rounded-bl-md text-sm text-foreground leading-relaxed whitespace-pre-wrap">
                                        {msg.content.message}
                                    </div>

                                    {/* Key Metrics */}
                                    {msg.content.key_metrics && msg.content.key_metrics.length > 0 && (
                                        <div className="grid grid-cols-2 gap-2">
                                            {msg.content.key_metrics.map((metric, i) => {
                                                const TrendIcon = TREND_ICONS[metric.trend] || null;
                                                return (
                                                    <div key={i} className="px-3 py-2.5 bg-card border border-border rounded-xl">
                                                        <div className="text-xs text-muted-foreground mb-1">{metric.label}</div>
                                                        <div className="flex items-center gap-1.5">
                                                            <span className="text-lg font-bold text-foreground">{metric.value}</span>
                                                            {TrendIcon && (
                                                                <TrendIcon size={14} className={
                                                                    metric.trend === 'up' ? 'text-emerald-500' :
                                                                        metric.trend === 'down' ? 'text-red-500' : 'text-muted-foreground'
                                                                } />
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {/* Insight type badge */}
                                    {msg.content.insight_type && (
                                        <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium border capitalize ${INSIGHT_COLORS[msg.content.insight_type] || INSIGHT_COLORS.overview}`}>
                                            {msg.content.insight_type}
                                        </span>
                                    )}

                                    {/* Suggestions */}
                                    {msg.content.suggestions && msg.content.suggestions.length > 0 && (
                                        <div className="flex flex-wrap gap-1.5">
                                            {msg.content.suggestions.map((s, i) => (
                                                <button
                                                    key={i}
                                                    onClick={() => sendMessage(null, s)}
                                                    className="px-3 py-1.5 bg-card border border-border rounded-lg text-xs text-muted-foreground hover:text-foreground hover:border-indigo-300 cursor-pointer transition-all"
                                                >
                                                    {s}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                ))}

                {isSending && (
                    <div className="flex gap-3">
                        <div className="w-7 h-7 min-w-[28px] rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center text-white">
                            <BarChart3 size={14} />
                        </div>
                        <div className="flex items-center gap-2 px-4 py-3 bg-muted/50 border border-border rounded-2xl rounded-bl-md">
                            <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-pulse"></div>
                            <span className="text-sm text-muted-foreground italic">Analyzing your data...</span>
                        </div>
                    </div>
                )}

                <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <form className="flex gap-2 p-3 bg-card border-t border-border" onSubmit={sendMessage}>
                <input
                    ref={inputRef}
                    type="text"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    placeholder="Ask about your data..."
                    disabled={isSending}
                    className="flex-1 px-4 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 placeholder:text-muted-foreground disabled:opacity-50"
                />
                <button
                    type="submit"
                    disabled={!inputMessage.trim() || isSending}
                    className="w-10 h-10 rounded-xl bg-gradient-to-r from-indigo-500 to-indigo-600 border-none text-white cursor-pointer flex items-center justify-center transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <Send size={16} />
                </button>
            </form>
        </div>
    );
};

export default AnalyticsAIPanel;
